import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../hooks/useAuth";
import { ArrowLeft, User, Clock, MessageSquare, Send, Loader2, Trash2, Pin, Pencil, ExternalLink, Image, X, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { MessageDialog } from "./Messages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = ["general", "introductions", "filmmaking", "technology", "festivals", "feedback"];

// Auto-link URLs in text
const LinkedText = ({ text, className = "" }) => {
  if (!text) return null;
  
  // Regex to match URLs
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    const url = match[1];
    // Try to extract a readable domain name
    let displayText = url;
    try {
      const urlObj = new URL(url);
      displayText = urlObj.hostname.replace('www.', '') + (urlObj.pathname !== '/' ? urlObj.pathname : '');
      if (displayText.length > 40) {
        displayText = displayText.slice(0, 37) + '...';
      }
    } catch {
      if (url.length > 40) {
        displayText = url.slice(0, 37) + '...';
      }
    }
    
    parts.push(
      <a
        key={match.index}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-purple-light hover:text-white transition-colors underline underline-offset-2"
      >
        {displayText}
        <ExternalLink className="w-3 h-3" />
      </a>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  if (parts.length === 0) {
    return <span className={className}>{text}</span>;
  }
  
  return <span className={className}>{parts}</span>;
};

export default function ForumPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState("");
  const [replyImageUrl, setReplyImageUrl] = useState(null);
  const [replyImagePreview, setReplyImagePreview] = useState(null);
  const [uploadingReplyImage, setUploadingReplyImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingReply, setDeletingReply] = useState(null);
  const [deletingPost, setDeletingPost] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editData, setEditData] = useState({ title: "", content: "", category: "general" });
  const [saving, setSaving] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const replyFileInputRef = useRef(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const isAdmin = user?.role === "admin" || user?.role === "super_admin" || user?.role === "content_moderator";
  const isAuthor = user && post?.author_id === user.user_id;
  const canEditDelete = isAdmin || isAuthor;

  useEffect(() => {
    fetchPost();
    fetchReplies();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error fetching post:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts/${id}/replies`);
      setReplies(response.data);
    } catch (error) {
      console.error("Error fetching replies:", error);
    }
  };

  const handleReplyImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPG, PNG, GIF, or WebP");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setReplyImagePreview(e.target.result);
    reader.readAsDataURL(file);

    setUploadingReplyImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/forum/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setReplyImageUrl(response.data.url);
      toast.success("Image uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
      setReplyImagePreview(null);
    } finally {
      setUploadingReplyImage(false);
    }
  };

  const removeReplyImage = () => {
    setReplyImagePreview(null);
    setReplyImageUrl(null);
    if (replyFileInputRef.current) {
      replyFileInputRef.current.value = '';
    }
  };

  const handleSubmitReply = async () => {
    if (!user) {
      toast.error("Please sign in to reply");
      navigate("/login");
      return;
    }

    if (!replyContent.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/forum/posts/${id}/replies`,
        { content: replyContent, image_url: replyImageUrl },
        { withCredentials: true }
      );
      toast.success("Reply posted!");
      setReplyContent("");
      setReplyImageUrl(null);
      setReplyImagePreview(null);
      fetchReplies();
      fetchPost(); // Refresh reply count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm("Are you sure you want to delete this post and all its replies? This cannot be undone.")) {
      return;
    }
    
    setDeletingPost(true);
    try {
      await axios.delete(`${API}/forum/posts/${id}`, { withCredentials: true });
      toast.success("Post deleted successfully");
      navigate("/forum");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete post");
    } finally {
      setDeletingPost(false);
    }
  };

  const handleTogglePin = async () => {
    setPinning(true);
    try {
      const response = await axios.patch(`${API}/forum/posts/${id}/pin`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchPost(); // Refresh post data to update pinned status
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update pin status");
    } finally {
      setPinning(false);
    }
  };

  const openEditModal = () => {
    setEditData({
      title: post.title,
      content: post.content,
      category: post.category || "general"
    });
    setIsEditOpen(true);
  };

  const handleEditPost = async () => {
    if (!editData.title.trim() || !editData.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/forum/posts/${id}`, editData, { withCredentials: true });
      toast.success("Post updated successfully");
      setIsEditOpen(false);
      fetchPost(); // Refresh post data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update post");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm("Are you sure you want to delete this reply?")) {
      return;
    }
    
    setDeletingReply(replyId);
    try {
      await axios.delete(`${API}/forum/posts/${id}/replies/${replyId}`, { withCredentials: true });
      toast.success("Reply deleted");
      fetchReplies();
      fetchPost(); // Refresh reply count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete reply");
    } finally {
      setDeletingReply(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-400 mb-4">Post not found</p>
        <Link to="/forum" className="text-purple-light hover:text-white">
          Back to Forum
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link 
            to="/forum" 
            data-testid="back-to-forum"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Forum
          </Link>

          {/* Post */}
          <article className={`p-6 bg-void-surface border mb-8 ${post.pinned ? 'border-purple/40 bg-purple/5' : 'border-white/5'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {post.pinned && (
                  <span className="flex items-center gap-1 text-xs text-purple-light bg-purple/20 px-2 py-0.5">
                    <Pin className="w-3 h-3" />
                    Pinned
                  </span>
                )}
                <span className="text-xs uppercase tracking-wider text-purple-light font-mono">
                  {post.category}
                </span>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(post.created_at).toLocaleDateString()}
                </span>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleTogglePin}
                    disabled={pinning}
                    data-testid="pin-post-btn"
                    className={`${post.pinned ? 'text-purple-light hover:text-purple' : 'text-slate-400 hover:text-purple-light'} hover:bg-purple/10`}
                    title={post.pinned ? "Unpin post" : "Pin post"}
                  >
                    {pinning ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Pin className="w-4 h-4" />
                    )}
                  </Button>
                  {canEditDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={openEditModal}
                      data-testid="edit-post-btn"
                      className="text-slate-400 hover:text-white hover:bg-white/10"
                      title="Edit post"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeletePost}
                    disabled={deletingPost}
                    data-testid="delete-post-btn"
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  >
                    {deletingPost ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}
              {!isAdmin && canEditDelete && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={openEditModal}
                    data-testid="edit-post-btn"
                    className="text-slate-400 hover:text-white hover:bg-white/10"
                    title="Edit post"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeletePost}
                    disabled={deletingPost}
                    data-testid="delete-post-btn"
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  >
                    {deletingPost ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
            
            <h1 className="font-syne text-2xl md:text-3xl font-bold text-white mb-6">
              {post.title}
            </h1>
            
            <div className="flex items-center gap-3 mb-6 pb-6 border-b border-white/5">
              <div className="w-10 h-10 bg-purple/20 flex items-center justify-center">
                <User className="w-5 h-5 text-purple-light" />
              </div>
              <span className="text-slate-300">{post.author_name}</span>
              {user && post.author_id !== user.user_id && (
                <button
                  onClick={() => setMessageRecipient({ user_id: post.author_id, name: post.author_name })}
                  className="ml-auto flex items-center gap-1 text-sm text-slate-400 hover:text-purple-light transition-colors"
                  title={`Message ${post.author_name}`}
                >
                  <Mail className="w-4 h-4" />
                  Message
                </button>
              )}
            </div>
            
            <div className="text-slate-300 whitespace-pre-wrap leading-relaxed">
              <LinkedText text={post.content} />
            </div>
            
            {/* Post Image */}
            {post.image_url && (
              <div className="mt-6">
                <img 
                  src={post.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${post.image_url}` : post.image_url}
                  alt="Post attachment"
                  className="max-w-full max-h-[500px] object-contain rounded-lg border border-white/10"
                />
              </div>
            )}
          </article>

          {/* Replies */}
          <div className="mb-8">
            <h2 className="font-syne font-bold text-white text-lg mb-6 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-light" />
              {replies.length} Replies
            </h2>
            
            {replies.length === 0 ? (
              <p className="text-slate-400 text-center py-8">
                No replies yet. Be the first to respond!
              </p>
            ) : (
              <div className="space-y-4">
                {replies.map((reply, index) => {
                  const canDelete = isAdmin || (user && reply.author_id === user.user_id);
                  return (
                    <motion.div
                      key={reply.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      data-testid={`reply-${index}`}
                      className="p-4 bg-void border border-white/5"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple/20 flex items-center justify-center">
                            <User className="w-4 h-4 text-purple-light" />
                          </div>
                          <span className="text-slate-300 text-sm">{reply.author_name}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(reply.created_at).toLocaleDateString()}
                          </span>
                          {user && reply.author_id !== user.user_id && (
                            <button
                              onClick={() => setMessageRecipient({ user_id: reply.author_id, name: reply.author_name })}
                              className="text-slate-500 hover:text-purple-light transition-colors p-1"
                              title={`Message ${reply.author_name}`}
                            >
                              <Mail className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteReply(reply.id)}
                            disabled={deletingReply === reply.id}
                            data-testid={`delete-reply-${index}`}
                            className="text-slate-500 hover:text-red-400 transition-colors p-1"
                            title="Delete reply"
                          >
                            {deletingReply === reply.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                      <p className="text-slate-300 text-sm whitespace-pre-wrap">
                        <LinkedText text={reply.content} />
                      </p>
                      {/* Reply Image */}
                      {reply.image_url && (
                        <div className="mt-3">
                          <img 
                            src={reply.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${reply.image_url}` : reply.image_url}
                            alt="Reply attachment"
                            className="max-w-full max-h-[300px] object-contain rounded-lg border border-white/10"
                          />
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reply Form */}
          <div className="p-6 bg-void-surface border border-white/5">
            <h3 className="font-syne font-bold text-white mb-4">Write a Reply</h3>
            {user ? (
              <>
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Share your thoughts..."
                  rows={4}
                  data-testid="reply-input"
                  className="bg-void border-white/10 text-white placeholder-slate-500 mb-4"
                />
                
                {/* Reply Image Upload */}
                <div className="mb-4">
                  <input
                    type="file"
                    ref={replyFileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleReplyImageUpload}
                    className="hidden"
                  />
                  
                  {replyImagePreview ? (
                    <div className="relative inline-block">
                      <img 
                        src={replyImagePreview} 
                        alt="Preview" 
                        className="h-32 object-cover rounded-lg border border-white/10"
                      />
                      <button
                        type="button"
                        onClick={removeReplyImage}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full transition-colors"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                      {uploadingReplyImage && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                          <Loader2 className="w-6 h-6 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => replyFileInputRef.current?.click()}
                      disabled={uploadingReplyImage}
                      className="flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-purple/50 rounded-lg transition-colors text-slate-400 hover:text-slate-300 text-sm"
                    >
                      {uploadingReplyImage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Image className="w-4 h-4" />
                      )}
                      Add Image
                    </button>
                  )}
                </div>

                <Button
                  onClick={handleSubmitReply}
                  disabled={submitting || uploadingReplyImage}
                  data-testid="submit-reply-btn"
                  className="bg-purple hover:bg-purple-hover text-white"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Post Reply
                </Button>
              </>
            ) : (
              <p className="text-slate-400">
                <Link to="/login" className="text-purple-light hover:text-white">
                  Sign in
                </Link>{" "}
                to reply to this post.
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Edit Post Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">Edit Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Title</label>
              <Input
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                placeholder="Post title"
                data-testid="edit-title-input"
                className="bg-void border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Category</label>
              <select
                value={editData.category}
                onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                data-testid="edit-category-select"
                className="w-full px-3 py-2 bg-void border border-white/10 text-white rounded-md"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Content</label>
              <Textarea
                value={editData.content}
                onChange={(e) => setEditData({ ...editData, content: e.target.value })}
                placeholder="Post content"
                rows={6}
                data-testid="edit-content-input"
                className="bg-void border-white/10 text-white"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setIsEditOpen(false)}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditPost}
                disabled={saving}
                data-testid="save-edit-btn"
                className="flex-1 bg-purple hover:bg-purple-hover text-white"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <MessageDialog
        isOpen={!!messageRecipient}
        onClose={() => setMessageRecipient(null)}
        recipient={messageRecipient || {}}
      />
    </div>
  );
}
